import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [isLoading, setIsLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: ''
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: 'Hello! Welcome to ELARA Education. I\'m here to help you with information about studying in Canada, our programs, and services. How can I assist you today?',
          timestamp: new Date()
        }
      ]);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: inputValue,
        user_info: leadFormData.email ? leadFormData : null
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date(response.data.timestamp)
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitLeadForm = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/leads/chatbot`, {
        name: leadFormData.name,
        email: leadFormData.email,
        phone: leadFormData.phone,
        location: leadFormData.location,
        message: `Chat session: ${sessionId}`,
        source: 'chatbot'
      });

      toast.success('Thank you! A consultant will reach out to you soon.');
      setShowLeadForm(false);
      
      const confirmMessage = {
        role: 'assistant',
        content: `Thank you ${leadFormData.name}! I've received your information. One of our education consultants will reach out to you shortly at ${leadFormData.email}. In the meantime, feel free to ask me any questions!`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, confirmMessage]);
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Failed to submit your information. Please try again.');
    }
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chatbot-toggle-button"
        className="fixed bottom-8 right-8 z-50 w-16 h-16 rounded-full bg-gradient-to-br from-secondary to-secondary-dark text-white shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center justify-center"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? <X size={28} /> : <MessageCircle size={28} />}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-28 right-8 z-50 w-full max-w-md h-[600px] bg-white shadow-2xl flex flex-col overflow-hidden"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-primary-light text-white p-6" data-testid="chatbot-header">
              <h3 className="font-semibold text-lg">ELARA Education Assistant</h3>
              <p className="text-sm text-slate-200 mt-1">Ask us anything about studying in Canada</p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50" data-testid="chatbot-messages">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`chatbot-message-${message.role}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-secondary text-primary'
                        : 'bg-white border border-slate-200 text-slate-800'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                  </div>
                </motion.div>
              ))}
              {isLoading && (
                <div className="flex justify-start" data-testid="chatbot-loading">
                  <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-secondary rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Lead Form */}
            {showLeadForm && (
              <div className="p-6 bg-white border-t border-slate-200" data-testid="chatbot-lead-form">
                <h4 className="font-semibold text-sm mb-3">Get Personalized Guidance</h4>
                <form onSubmit={submitLeadForm} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={leadFormData.name}
                    onChange={(e) => setLeadFormData({ ...leadFormData, name: e.target.value })}
                    required
                    data-testid="lead-form-name"
                    className="w-full px-3 py-2 border border-slate-200 text-sm focus:outline-none focus:border-secondary"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={leadFormData.email}
                    onChange={(e) => setLeadFormData({ ...leadFormData, email: e.target.value })}
                    required
                    data-testid="lead-form-email"
                    className="w-full px-3 py-2 border border-slate-200 text-sm focus:outline-none focus:border-secondary"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={leadFormData.phone}
                    onChange={(e) => setLeadFormData({ ...leadFormData, phone: e.target.value })}
                    required
                    data-testid="lead-form-phone"
                    className="w-full px-3 py-2 border border-slate-200 text-sm focus:outline-none focus:border-secondary"
                  />
                  <input
                    type="text"
                    placeholder="Current Location"
                    value={leadFormData.location}
                    onChange={(e) => setLeadFormData({ ...leadFormData, location: e.target.value })}
                    required
                    data-testid="lead-form-location"
                    className="w-full px-3 py-2 border border-slate-200 text-sm focus:outline-none focus:border-secondary"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      data-testid="lead-form-submit"
                      className="flex-1 px-4 py-2 bg-secondary text-primary font-semibold text-sm hover:bg-secondary-light transition-colors"
                    >
                      Submit
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowLeadForm(false)}
                      data-testid="lead-form-cancel"
                      className="px-4 py-2 border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Input Area */}
            {!showLeadForm && (
              <div className="p-4 bg-white border-t border-slate-200" data-testid="chatbot-input-area">
                {!leadFormData.email && (
                  <button
                    onClick={() => setShowLeadForm(true)}
                    data-testid="show-lead-form-button"
                    className="w-full mb-3 px-4 py-2 bg-secondary/10 text-secondary text-sm font-medium hover:bg-secondary/20 transition-colors"
                  >
                    Talk to a Consultant
                  </button>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    disabled={isLoading}
                    data-testid="chatbot-input"
                    className="flex-1 px-4 py-3 border border-slate-200 text-sm focus:outline-none focus:border-secondary disabled:bg-slate-100"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={isLoading || !inputValue.trim()}
                    data-testid="chatbot-send-button"
                    className="px-4 py-3 bg-secondary text-primary hover:bg-secondary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={20} />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};